import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { AlertDialog, ThreeDots } from '@components/ui';
import { ThreeDotItem } from '@components/ui/ThreeDots/ThreeDots';
import { useDownloadSignedDocumentByIdMutation } from '@dieterApi/signature/useDownloadSignedDocumentMutation';
import {
  ISignatureRequest,
  useCreateSignatureRequestMutation,
} from '@dieterApi/signature/useSignatureRequestCreateMutation';
import { useDeleteSignatureRequestMutation } from '@dieterApi/signature/useSignatureRequestDeleteMutation';
import { useResendSignatureRequestMutation } from '@dieterApi/signature/useSignatureRequestResendMutation';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Chip, Collapse, FormControlLabel, IconButton, Skeleton, Switch, TextField, Tooltip } from '@mui/material';
import TableCell from '@mui/material/TableCell/TableCell';
import TableRow from '@mui/material/TableRow/TableRow';
import cx from 'classnames';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { useDashboard } from '../../../useDashboard';
import { SignedContractModal } from './SignedContractModal';

import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { SignaturePositionSelector, SignaturePositions } from './SignaturePositionSelector';

interface Props {
  contractId: string;
  colSpan?: number;
  onClose?: () => void;
  tableMeta: any;
  topic: any;
  setManageSubModalOpen?: (open: boolean) => void;
}

interface CreateRequestFormValues {
  firstName1: string;
  lastName1: string;
  email1: string;
  firstName2: string;
  lastName2: string;
  email2: string;
  includeSubcontractors: boolean;
  includeToms: boolean;
}

export function SignatureRequests({ contractId, colSpan, onClose, tableMeta, topic, setManageSubModalOpen }: Props) {
  const { user, activeCompany, questionnaires } = useUserWithCompany();
  const { getValue, userValues } = useUserValuesData();
  const { enqueueSnackbar } = useSnackbar();
  const [createRequest, { loading: createLoading }] = useCreateSignatureRequestMutation();
  const [resendRequest, { loading: resendLoading }] = useResendSignatureRequestMutation();
  const dashboard = useDashboard();

  // const { data, loading } = useListSignatureRequestQuery({ questionnaireId: questionnaire?.id || '' });

  const [deleteRequest] = useDeleteSignatureRequestMutation();
  const [downloadSignedDocument] = useDownloadSignedDocumentByIdMutation();
  const emptyForm: CreateRequestFormValues = {
    firstName1: '',
    lastName1: '',
    email1: '',
    firstName2: '',
    lastName2: '',
    email2: '',
    includeSubcontractors: false,
    includeToms: false,
  };

  const [createNew, setCreateNew] = useState(false);
  const [formValues, setFormValues] = useImmer<CreateRequestFormValues>(emptyForm);
  const signatureRequest = activeCompany?.signatureRequests?.find(
    (sr) => sr.id === getValue('DATA_PROCESSOR_SIGNATURE_REQUEST', contractId)
  );

  const loading = false;

  const [alertOpen, setAlertOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [signedContractModalOpen, setSignedContractModalOpen] = useState(false);
  const [showPositionSelector, setShowPositionSelector] = useState(false);
  const [signaturePositions, setSignaturePositions] = useState<SignaturePositions | null>(null);

  // Check if this contract uses an uploaded file (vs generated questionnaire)
  const contractFileId = getValue('DATA_PROCESSOR_CONTRACTFILE', contractId);
  const hasContractFile = Boolean(contractFileId);

  // Check if TOMs questionnaire exists and is complete
  const hasToms = Array.isArray(questionnaires)
    ? questionnaires.some((q) => q.application?.id === 'app-toms-create' && q.state === 'COMPLETE')
    : false;

  // Find the incomplete or existing TOMs questionnaire (if any)
  const tomsQuestionnaire = Array.isArray(questionnaires)
    ? questionnaires.find((q) => q.application?.id === 'app-toms-create')
    : undefined;

  // Check if at least one subcontractor is enabled for this partner
  function getSubcontractorIndices(userValues: any, mainIndex: string): string[] {
    if (!userValues) return [];
    const uv = userValues.find((uv: any) => uv.key === 'DATA_PROCESSOR_HASSUBCONTRACTOR' && uv.index === mainIndex);
    if (!uv) return [];
    try {
      const arr = JSON.parse(uv.value);
      if (Array.isArray(arr)) {
        return arr.filter((v) => typeof v === 'string');
      }
      return [];
    } catch {
      return [];
    }
  }
  const hasSubcontractors = getSubcontractorIndices(userValues, contractId).length > 0;
  const isContractor = getValue('DATA_PROCESSOR_DIRECTION', contractId) === 'Ich bin der Auftragnehmer';

  const handleCreate = () => {
    if (!createNew) {
      setFormValues((draft) => {
        draft.firstName1 = user?.firstName || '';
        draft.lastName1 = user?.lastName || '';
        draft.email1 = user?.email || '';
        draft.includeSubcontractors = false;
        draft.includeToms = false;
      });
      setCreateNew(true);
    } else {
      setCreateNew(false);
    }
  };

  const handleSend = () => {
    // For uploaded file contracts, require signature positions
    if (hasContractFile && !signaturePositions) {
      setShowPositionSelector(true);
      return;
    }

    createRequest({
      variables: {
        contractId: contractId || '',
        signer: [
          {
            email: formValues.email1,
            first_name: formValues.firstName1,
            last_name: formValues.lastName1,
          },
          {
            email: formValues.email2,
            first_name: formValues.firstName2,
            last_name: formValues.lastName2,
          },
        ],
        includeSubcontractors: formValues.includeSubcontractors,
        includeToms: formValues.includeToms,
        ...(signaturePositions
          ? {
              signature1Position: signaturePositions.signature1,
              signature2Position: signaturePositions.signature2,
            }
          : {}),
      },
    })
      .then(() => {
        setCreateNew(false);
        setFormValues(emptyForm);
        setSignaturePositions(null);
        setShowPositionSelector(false);
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handlePositionsSelected = (positions: SignaturePositions) => {
    setSignaturePositions(positions);
    setShowPositionSelector(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleDelete = (id: string) => {
    setPendingDeleteId(id);
    setAlertOpen(true);
  };

  const handleResend = (signeeId: string) => {
    if (!signatureRequest) return;

    resendRequest({
      variables: {
        requestId: signatureRequest.id,
        signeeId,
      },
    })
      .then(() => {
        enqueueSnackbar('E-Mail erfolgreich erneut versendet', { variant: 'success' });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleDownloadSigned = (id: string) => {
    downloadSignedDocument({ variables: { id } })
      .then(({ data }) => {
        if (!data) return;
        const { fileBase64, fileName, mimeType } = data.downloadSignedDocumentById;
        const byteCharacters = atob(fileBase64);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const confirmDelete = () => {
    if (pendingDeleteId) {
      deleteRequest({ variables: { id: pendingDeleteId } }).catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
    }
    setAlertOpen(false);
    setPendingDeleteId(null);
  };

  const cancelDelete = () => {
    setAlertOpen(false);
    setPendingDeleteId(null);
  };

  const threeDotItems = (signatureRequest: ISignatureRequest): ThreeDotItem[] => {
    const items: ThreeDotItem[] = [];
    if (signatureRequest.hasSigned1 && signatureRequest.hasSigned2) {
      items.push({
        label: 'Signiertes Dokument herunterladen',
        onClick: () => handleDownloadSigned(signatureRequest.id),
        icon: <DownloadIcon fontSize="small" />,
      });
    }
    items.push({
      label: 'Signiervorgang löschen',
      onClick: () => handleDelete(signatureRequest.id),
      icon: <DeleteForeverIcon fontSize="small" />,
    });
    return items;
  };

  return (
    <TableRow className="dtSignatureRequests">
      <TableCell colSpan={colSpan}>
        <div className="flex flex-col gap-[10px]">
          <div className="flex justify-between">
            <div className="flex gap-[10px] items-center">
              {!createNew &&
                !signatureRequest &&
                (() => {
                  const signedContractFileId = getValue('DATA_PROCESSOR_SIGNED_CONTRACT', contractId);
                  const hasSignedContract = Boolean(signedContractFileId);
                  return (
                    <>
                      <button
                        onClick={handleCreate}
                        className={cx(
                          'underline ',
                          hasSignedContract ? 'text-gray-400 cursor-not-allowed!' : 'text-primary-root'
                        )}
                        disabled={hasSignedContract}
                      >
                        Neuer Signiervorgang
                      </button>
                      <span style={{ margin: '0 8px' }}>|</span>
                      <button onClick={() => setSignedContractModalOpen(true)} className="underline text-primary-root">
                        {hasSignedContract
                          ? 'Unterschriebenen Vertrag verwalten'
                          : 'Unterschriebenen Vertrag hochladen'}
                      </button>
                    </>
                  );
                })()}
            </div>
            <button
              onClick={onClose}
              className={cx(createNew ? 'text-gray-400 cursor-not-allowed!' : 'text-primary-root underline')}
              disabled={createNew}
            >
              Ausblenden
            </button>
          </div>
          <Collapse in={createNew}>
            <div className="newSignatureRequest flex gap-[10px] flex-col">
              <AlertDialog
                open={alertOpen}
                setOpen={setAlertOpen}
                title={'Signiervorgang löschen'}
                message={'Möchtest du diesen Signiervorgang wirklich löschen?'}
                onAccept={confirmDelete}
                onCancel={cancelDelete}
                acceptText={'Löschen'}
              />
              <div>
                <span className="signer-label">Erste unterzeichnende Person</span>
                <div className="flex gap-[10px] items-center">
                  <TextField
                    className="flex-1"
                    name="firstName1"
                    label="Vorname"
                    size="small"
                    required={true}
                    variant="standard"
                    onChange={handleInputChange}
                    value={formValues.firstName1}
                  />
                  <TextField
                    className="flex-1"
                    name="lastName1"
                    label="Nachname"
                    size="small"
                    required={true}
                    variant="standard"
                    onChange={handleInputChange}
                    value={formValues.lastName1}
                  />
                  <TextField
                    className="flex-2"
                    name="email1"
                    label="E-Mail"
                    size="small"
                    required={true}
                    variant="standard"
                    onChange={handleInputChange}
                    value={formValues.email1}
                  />
                </div>
              </div>
              <div>
                <span className="signer-label">Zweite unterzeichnende Person</span>
                <div className="flex gap-[10px] items-center">
                  <TextField
                    className="flex-1"
                    name="firstName2"
                    label="Vorname"
                    size="small"
                    required={true}
                    variant="standard"
                    onChange={handleInputChange}
                    value={formValues.firstName2}
                  />
                  <TextField
                    className="flex-1"
                    name="lastName2"
                    label="Nachname"
                    size="small"
                    required={true}
                    variant="standard"
                    onChange={handleInputChange}
                    value={formValues.lastName2}
                  />
                  <TextField
                    className="flex-2"
                    name="email2"
                    label="E-Mail"
                    size="small"
                    required={true}
                    variant="standard"
                    onChange={handleInputChange}
                    value={formValues.email2}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-4 ">
                <div>
                  <FormControlLabel
                    control={
                      <Switch
                        name="includeToms"
                        checked={formValues.includeToms}
                        onChange={handleInputChange}
                        color="primary"
                        disabled={!hasToms}
                      />
                    }
                    label="TOMs-Dokument beilegen"
                  />
                  {
                    <button
                      type="button"
                      className="ml-2 text-xs underline text-primary-root"
                      onClick={() => {
                        dashboard.create(
                          'app-toms-create',
                          undefined,
                          true,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          true
                        );
                      }}
                    >
                      TOMs {!hasToms ? 'fertigstellen' : 'bearbeiten'}
                    </button>
                  }
                </div>
                {isContractor ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <FormControlLabel
                        control={
                          <Switch
                            name="includeSubcontractors"
                            checked={formValues.includeSubcontractors}
                            onChange={handleInputChange}
                            color="primary"
                            disabled={!hasSubcontractors}
                          />
                        }
                        label="Subunternehmerliste beilegen"
                      />
                      {setManageSubModalOpen && (
                        <button
                          type="button"
                          className="ml-2 text-xs underline text-primary-root"
                          onClick={() => setManageSubModalOpen(true)}
                        >
                          Subunternehmer verwalten
                        </button>
                      )}
                    </div>
                    {/* Warning if any selected subcontractor has no address */}
                    {(() => {
                      const subIndices = getSubcontractorIndices(userValues, contractId);
                      if (!subIndices.length) return null;
                      const missing = subIndices.filter((idx) => {
                        const uv = userValues?.find(
                          (uv: any) => uv.key === 'DATA_PROCESSOR_ADDRESS' && uv.index === idx
                        );
                        return !uv || !uv.value?.trim();
                      });
                      if (missing.length > 0) {
                        return (
                          <div className="text-xs text-orange-600 mt-1">
                            Achtung: Für {missing.length} Subunternehmer ist keine Adresse hinterlegt!
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <span className="italic text-sm text-gray-500">
                    Subunternehmerliste nicht relevant, da du Auftraggeber bist
                  </span>
                )}
              </div>
            </div>
          </Collapse>

          {createNew && (
            <>
              {/* Signature Position Selector modal for uploaded file contracts */}
              {hasContractFile && contractFileId && (
                <SignaturePositionSelector
                  open={showPositionSelector}
                  fileId={contractFileId}
                  onPositionsSelected={handlePositionsSelected}
                  onCancel={() => setShowPositionSelector(false)}
                />
              )}

              {/* Position status indicator */}
              {hasContractFile && signaturePositions && !showPositionSelector && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                  <span>✓ Unterschriftspositionen festgelegt</span>
                  <button className="underline text-primary-root text-xs" onClick={() => setShowPositionSelector(true)}>
                    Ändern
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-[10px] align-self-end">
                <Link to="#" onClick={handleCreate}>
                  Abbrechen
                </Link>
                <Link to="#" onClick={handleSend}>
                  {hasContractFile && !signaturePositions ? 'Positionen festlegen' : 'Versenden'}
                </Link>
              </div>
            </>
          )}

          {loading ? (
            <Skeleton />
          ) : (
            <>
              {signatureRequest && (
                <div className="flex flex-col gap-3">
                  <div key={signatureRequest.id} className="flex gap-[10px] justify-between items-center">
                    <div className="flex-1">
                      Signaturanfrage erstellt am{' '}
                      <strong>{new Date(signatureRequest.createdAt).toLocaleDateString()}</strong>
                    </div>
                    <ThreeDots items={threeDotItems(signatureRequest)} />
                  </div>
                  <table className="w-full">
                    <tbody>
                      <tr>
                        <td className="pl-0 pr-4 py-1 text-left align-middle whitespace-nowrap">
                          <span className="text-gray-500">{signatureRequest.signee1.name}</span>
                        </td>
                        <td className="pl-0 pr-4 py-1 text-left align-middle whitespace-nowrap">
                          <span className="text-gray-500">{signatureRequest.signee1.email}</span>
                        </td>
                        <td className="pl-0 py-1 text-right align-middle">
                          <RequestStatus
                            hasSigned={signatureRequest.hasSigned1}
                            signature={signatureRequest.signature1}
                            signeeId={signatureRequest.signee1.id}
                            onResend={handleResend}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="pl-0 pr-4 py-1 text-left align-middle whitespace-nowrap">
                          <span className="text-gray-500">{signatureRequest.signee2.name}</span>
                        </td>
                        <td className="pl-0 pr-4 py-1 text-left align-middle whitespace-nowrap">
                          <span className="text-gray-500">{signatureRequest.signee2.email}</span>
                        </td>
                        <td className="pl-0 py-1 text-right align-middle">
                          <RequestStatus
                            hasSigned={signatureRequest.hasSigned2}
                            signature={signatureRequest.signature2}
                            signeeId={signatureRequest.signee2.id}
                            onResend={handleResend}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Attachments indicator */}
                  {((signatureRequest.includeToms && typeof signatureRequest.includeToms === 'boolean') ||
                    (signatureRequest.includeSubcontractors &&
                      typeof signatureRequest.includeSubcontractors === 'boolean')) && (
                    <div className="text-xs text-gray-500 mt-1">
                      Anlagen:{'  '}
                      {[
                        signatureRequest.includeToms ? 'TOMs' : null,
                        signatureRequest.includeSubcontractors ? 'Subunternehmerliste' : null,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <SignedContractModal
            open={signedContractModalOpen}
            onClose={() => setSignedContractModalOpen(false)}
            topic={topic}
            tableMeta={tableMeta}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

interface RequestStatusProps {
  hasSigned: boolean;
  signature?: string;
  signeeId: string;
  onResend: (signeeId: string) => void;
}

function RequestStatus({ hasSigned, signature, signeeId, onResend }: RequestStatusProps) {
  if (!hasSigned) {
    return (
      <div className="flex items-center gap-2 justify-end">
        <Chip label="Anfrage versendet" color="default" size="small" sx={{ color: 'primary' }} />
        <Tooltip title="E-Mail erneut versenden" placement="top" arrow>
          <IconButton
            size="small"
            onClick={() => onResend(signeeId)}
            className="text-primary-root hover:text-primary-dark"
            aria-label="E-Mail erneut versenden"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    );
  } else if (signature) {
    return (
      <Tooltip
        title={<img src={signature} alt="Unterschrift" style={{ maxWidth: 200, maxHeight: 80 }} />}
        placement="top"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: '#fff',
              color: '#222',
              boxShadow: 2,
              border: '1px solid #eee',
            },
          },
        }}
      >
        <span>
          <Chip label="Unterschrieben" color="success" size="small" sx={{ color: 'white' }} />
        </span>
      </Tooltip>
    );
  } else {
    return <Chip label="Unterschrieben" color="success" size="small" sx={{ color: 'white' }} />;
  }
}
